import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import Loading from '../components/Loading';
import { listTeam } from '../services/team';
import { coverageCaption, coverageOn, listSuppliers } from '../services/suppliers';
import { listParts } from '../services/parts';
import { listPlants } from '../services/plants';
import { findSubmitterMember, scopedPlantCodes } from '../utils/filterScope';
import {
  resolveSupplierScope,
  scopeAllowsSupplierAtPlant,
  scopedSupplierCodes,
} from '../utils/supplierScope';
import {
  COVERAGE_TYPE_LABELS,
  DAY_KEYS,
  OT_RATE_MULTIPLIER,
  STATUS,
  approveTimesheet,
  listTimesheetsByStatus,
  explanationLines,
  rejectTimesheet,
  stripEmptyRows,
  updateApprovedTimesheet,
  weekDates,
} from '../services/timesheets';
import { calcMileageDollars } from '../config/rates';
import { aggregateBilling, presetRange } from '../services/billing';
import {
  STD_OVER_COLOR,
  STD_UNDER_COLOR,
  rowIsOverStandard,
  standardHoursDeviations,
  weeklyStandardDeviation,
} from './Timesheets';

/**
 * Timesheet Approvals — Regional Managers see Pending timesheets from
 * their direct reports (filtered by matching the submitter's stored
 * RegionalManager against the RM's display name). Directors see every
 * Pending timesheet across the company. Each card shows a full
 * read-only summary of the submitted week; Approve locks it and stamps
 * ApprovedBy/ApprovedAt, Reject bounces it back to Draft with an
 * optional reason so the submitter knows what to fix.
 *
 * A simple page-level refresh after every action keeps the list fresh
 * without needing reconciliation logic — the list is small enough that
 * a re-fetch after each approval is effectively free.
 */
export default function TimesheetApprovals({ selfApprovedView = false }) {
  // selfApprovedView (per Jillian, July 2026) is the Liaison Manager
  // "Approved Trackers" tab: the SAME page and search bar, but read-only
  // and scoped to only the viewer's OWN approved submissions — no
  // Pending / Rejected sections, no approve/reject actions.
  const { name, email, isDirector, isRegionalManager, isLiaisonManager } =
    useAuth();
  const navigate = useNavigate();

  const [pending, setPending] = useState([]);
  const [approvedList, setApprovedList] = useState([]);
  const [rejectedList, setRejectedList] = useState([]);
  const [team, setTeam] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [parts, setParts] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(null); // timesheet id currently being approved/rejected
  const [actionError, setActionError] = useState('');

  // Date-range filter. Mirrors the Billing dashboard's preset/from/to
  // pattern. The default is "This Month" so the page lands focused
  // on the current billing/coverage period — wide enough to catch
  // anything still in flight and aligned with how RMs think about
  // the workweek calendar. A timesheet's WeekStart (Monday-of-week)
  // is the key we filter on; the range controls update both startDate
  // and endDate together.
  const defaultRange = useMemo(() => presetRange('this-month'), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  // Which preset (if any) is currently active. Set when the user
  // clicks a preset button; cleared the moment they hand-edit either
  // date picker so the highlighted button always reflects the actual
  // range. Lands on 'this-month' on first render to match the default.
  const [activePreset, setActivePreset] = useState('this-month');
  const applyPreset = (preset) => {
    const { startDate: s, endDate: e } = presetRange(preset);
    setStartDate(s);
    setEndDate(e);
    setActivePreset(preset);
  };
  // A timesheet falls in range if its WeekStart is between
  // startDate and endDate (inclusive). String compare is safe because
  // both sides are canonical YYYY-MM-DD ISO dates.
  const inRange = useCallback(
    (t) => {
      const ws = (t.WeekStart || '').slice(0, 10);
      if (!ws) return false;
      if (startDate && ws < startDate) return false;
      if (endDate && ws > endDate) return false;
      return true;
    },
    [startDate, endDate],
  );

  // Plant filter — empty string = "All plants". When set to a plant code,
  // a tracker is kept if it has a row at that plant, and the grid then
  // shows only that plant's line items (see matchesRowFilters + the card's
  // row-level filtering). Rowless adjustments (credits) stay visible.
  const [plantFilter, setPlantFilter] = useState('');

  // Supplier filter — same pattern as the plant filter. Options pull from
  // the full suppliers list (NOT narrowed by the current plant filter) so
  // the dropdown can find a supplier that only covers a specific plant.
  // Combined with the plant filter, a director can scope to a single
  // (plant × supplier) pair.
  const [supplierFilter, setSupplierFilter] = useState('');

  // Billing Type filter — empty string = "All billing types". Matches a
  // row's coverageType ('Standard' / 'Overage' / 'Mileage' / 'Expenses').
  // Same row-level behavior as plant / supplier: the grid shows only the
  // matching line items and totals recompute (per Jillian, Aug 2026).
  const [coverageFilter, setCoverageFilter] = useState('');

  // Overages Only (Nov 2026) — keep just the Standard rows billed OVER
  // their standard coverage (the red cue), and the trackers that have one.
  const [overagesOnly, setOveragesOnly] = useState(false);

  // Combined plant + supplier + billing-type matcher. A tracker appears
  // only when it has at least one row matching EVERY active filter (all on
  // the same row when several are set), so the list-level visibility agrees
  // with the row-level grid below — no cards that then show "no line items
  // match". Rowless adjustments (credits) stay visible while a filter is
  // active.
  const matchesRowFilters = useCallback(
    (t) => {
      if (!plantFilter && !supplierFilter && !coverageFilter && !overagesOnly) return true;
      const rows = Array.isArray(t.rows) ? t.rows : [];
      if (rows.length === 0) return !overagesOnly && t.IsAdjustment === true;
      const tDates = overagesOnly && t.WeekStart ? weekDates(t.WeekStart) : null;
      const p = plantFilter.trim().toUpperCase();
      const s = supplierFilter.trim().toUpperCase();
      return rows.some((r) => {
        if (
          plantFilter &&
          String(r.plantCode || '').trim().toUpperCase() !== p
        ) {
          return false;
        }
        if (
          supplierFilter &&
          String(r.supplierCode || '').trim().toUpperCase() !== s
        ) {
          return false;
        }
        if (coverageFilter && String(r.coverageType || '') !== coverageFilter) {
          return false;
        }
        if (overagesOnly && !rowIsOverStandard(r, plants, suppliers, tDates)) {
          return false;
        }
        return true;
      });
    },
    [plantFilter, supplierFilter, coverageFilter, overagesOnly, plants, suppliers],
  );

  // Regional Manager filter — empty string = "All RMs". Matches on
  // the timesheet's denormalized RegionalManager field (set at
  // submission time so later roster changes don't orphan history).
  // For non-Director RMs the page is already scoped to their own
  // reports, so this filter is effectively a no-op for them; for
  // Directors who see every RM's queue it lets them home in on a
  // single RM's work without scrolling.
  const [rmFilter, setRmFilter] = useState('');
  const matchesRm = useCallback(
    (t) => {
      if (!rmFilter) return true;
      const target = rmFilter.trim().toLowerCase();
      const stored = (t.RegionalManager || '').trim().toLowerCase();
      // "Unassigned" maps to timesheets with no RM set, since that's
      // how the dropdown surfaces them. Any literal RM named
      // "Unassigned" would clash with this — acceptable in practice
      // because nobody on the roster carries that name.
      if (target === 'unassigned') return stored === '';
      return stored === target;
    },
    [rmFilter],
  );

  // Submitter filter — empty string = "All submitters". Key is the
  // SubmitterId when present (stable across name / email changes);
  // legacy rows missing a SubmitterId fall back to a key derived
  // from the lowercased SubmitterName so they still appear and
  // remain selectable. Useful for Directors who want to see every
  // timesheet one team member has filed across the period without
  // scrolling through each RM section.
  const submitterKey = useCallback((t) => {
    const id = String(t?.SubmitterId || '').trim();
    if (id) return id;
    return (t?.SubmitterName || '').trim().toLowerCase();
  }, []);
  const [submitterFilter, setSubmitterFilter] = useState('');
  const matchesSubmitter = useCallback(
    (t) => {
      if (!submitterFilter) return true;
      return submitterKey(t) === submitterFilter;
    },
    [submitterFilter, submitterKey],
  );

  // Set of approved-card IDs the user has expanded. Default is
  // collapsed, so the page lands compact and lets the RM scan the
  // headers (submitter / week / dates) without scrolling past every
  // detail grid.
  const [expandedApproved, setExpandedApproved] = useState(() => new Set());

  // Per-week collapse state. Default behavior is computed at render
  // time (Pending weeks default open so the action queue is visible,
  // Approved weeks default closed since it's a history view); this
  // Set tracks the IDs the user has *toggled* away from that default,
  // same XOR pattern the proposals year-collapse uses elsewhere.
  // Keyed by "section|rm|weekStart" so multiple sections / RMs don't
  // share collapse state by accident.
  const [toggledWeeks, setToggledWeeks] = useState(() => new Set());
  const toggleWeek = (key) =>
    setToggledWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const isWeekExpanded = (key, defaultOpen) =>
    toggledWeeks.has(key) ? !defaultOpen : defaultOpen;

  // Plant-level collapse state — parallel to the week-level state
  // above. Used by the RM → Plant → Week hierarchy so each plant
  // section can independently expand/collapse without losing its
  // child weeks' state. Keyed by "section|rm|plant" so two RMs that
  // both cover the same plant don't share collapse state.
  const [toggledPlants, setToggledPlants] = useState(() => new Set());
  const togglePlant = (key) =>
    setToggledPlants((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const isPlantExpanded = (key, defaultOpen) =>
    toggledPlants.has(key) ? !defaultOpen : defaultOpen;


  const normName = useMemo(() => (name || '').trim().toLowerCase(), [name]);

  // The viewer's own team-record id, resolved by Proxemics email. Used
  // so an RM's OWN submissions land in their approval inbox — an RM has
  // no RM above them, so their timesheet's stored RegionalManager is
  // blank and the name-based scoping below would otherwise hide it
  // (per Jillian, July 2026: RMs approve their own submissions).
  const myTeamId = useMemo(() => {
    const norm = (email || '').trim().toLowerCase();
    if (!norm) return '';
    const mine = (team || []).find(
      (m) => (m.Email || '').trim().toLowerCase() === norm,
    );
    return mine ? String(mine.id) : '';
  }, [team, email]);
  const isOwnSubmission = useCallback(
    (t) => {
      const sid = String(t?.SubmitterId || '').trim();
      if (sid && myTeamId && sid === myTeamId) return true;
      // Fallback for legacy rows with no SubmitterId — match by name.
      return (t?.SubmitterName || '').trim().toLowerCase() === normName;
    },
    [myTeamId, normName],
  );

  // Whether a timesheet is within THIS viewer's approval scope — the
  // same rule the Pending / Approved lists use. Directors see everyone;
  // an LM self-view sees only their own; a Regional Manager sees their
  // direct reports (the submitter's stored RegionalManager matches the
  // RM's name) plus their own submissions. Used to scope the RM /
  // Submitter filter dropdowns so an RM only sees the managers they
  // actually oversee — not everyone across the org.
  const inViewerScope = useCallback(
    (t) => {
      if (isDirector) return true;
      if (selfApprovedView) return isOwnSubmission(t);
      if (isRegionalManager) {
        return (
          (t.RegionalManager || '').trim().toLowerCase() === normName ||
          isOwnSubmission(t)
        );
      }
      return false;
    },
    [isDirector, selfApprovedView, isRegionalManager, normName, isOwnSubmission],
  );

  // RM dropdown options — distinct RegionalManager values across the
  // timesheets IN THIS VIEWER'S SCOPE, so the list only offers RMs the
  // viewer is allowed to see. Sorted alphabetically; rows without an RM
  // contribute an "Unassigned" bucket.
  const rmOptions = useMemo(() => {
    const set = new Set();
    const everyTimesheet = [
      ...(pending || []),
      ...(approvedList || []),
      ...(rejectedList || []),
    ].filter(inViewerScope);
    for (const t of everyTimesheet) {
      const rm = (t.RegionalManager || '').trim();
      if (rm) set.add(rm);
      else set.add('Unassigned');
    }
    return Array.from(set).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
  }, [pending, approvedList, rejectedList, inViewerScope]);

  // Submitter dropdown options — distinct submitters across the
  // timesheets IN THIS VIEWER'S SCOPE. For a Regional Manager this is
  // exactly the managers they oversee (plus their own submissions), so
  // the dropdown never lists someone else's team. Keyed by submitterKey
  // so a rename doesn't split into two entries; sorted by name.
  const submitterOptions = useMemo(() => {
    const map = new Map();
    const everyTimesheet = [
      ...(pending || []),
      ...(approvedList || []),
      ...(rejectedList || []),
    ].filter(inViewerScope);
    for (const t of everyTimesheet) {
      const key = submitterKey(t);
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: (t.SubmitterName || '').trim() || '(unnamed)',
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
  }, [pending, approvedList, rejectedList, submitterKey, inViewerScope]);

  // The viewer's own assigned plant codes (from their Team record). In
  // the LM self-view the Plant + Supplier dropdowns are narrowed to
  // these plants (and the suppliers that cover them), per Jillian,
  // July 2026. Empty set when we can't resolve their record.
  const myPlantCodesSet = useMemo(() => {
    const set = new Set();
    const normEmail = (email || '').trim().toLowerCase();
    const normName = (name || '').trim().toLowerCase();
    for (const m of team || []) {
      const memberEmail = (m.Email || '').trim().toLowerCase();
      const memberRm = (m.RegionalManager || '').trim().toLowerCase();
      // Include a plant if it belongs to the viewer's OWN record (LM
      // self-view / an RM's own assignments) OR — for a Regional Manager —
      // to any team member they oversee (member.RegionalManager === my
      // name). This gives an RM the union of all their liaisons' plants.
      // Directors don't use this set (they see the full lists).
      const mine =
        (normEmail && memberEmail === normEmail) ||
        (normName && memberRm === normName);
      if (!mine) continue;
      for (const c of Array.isArray(m.plantCodes) ? m.plantCodes : []) {
        const code = String(c || '').toUpperCase().trim();
        if (code) set.add(code);
      }
    }
    return set;
  }, [team, email, name]);
  // Plant + supplier dropdown option lists. Scoped to the viewer's assigned
  // plants (and the suppliers that cover them) for everyone EXCEPT
  // Directors, who see the full lists. (LM self-view and RMs are both
  // scoped, per Jillian, Aug 2026.)
  const plantChoices = useMemo(() => {
    const base = (plants || []).filter((p) => p.PlantCode);
    if (isDirector) return base;
    return base.filter((p) =>
      myPlantCodesSet.has(String(p.PlantCode).toUpperCase().trim()),
    );
  }, [plants, isDirector, myPlantCodesSet]);

  // When a specific person is chosen in "Submitted By" or "Regional Manager",
  // limit the Plant dropdown to that person's assigned plant(s) — and if they
  // have exactly one, select it automatically (per Jillian, Sep 2026).
  const plantScope = useMemo(
    () =>
      scopedPlantCodes(team, {
        submitterKey: submitterFilter,
        rmName: rmFilter,
      }),
    [team, submitterFilter, rmFilter],
  );
  useEffect(() => {
    if (!plantScope) return; // no person selected → leave Plant alone
    setPlantFilter((prev) => {
      if (plantScope.size === 1) return [...plantScope][0];
      if (prev && !plantScope.has(String(prev).toUpperCase())) return '';
      return prev;
    });
  }, [plantScope]);
  // Supplier dropdown follows the other filters (per Jillian, Oct 2026):
  //   • a Plant is picked → only suppliers assigned at that plant;
  //   • a Liaison Manager is picked in "Submitted By" → only the suppliers
  //     they're responsible for (all suppliers at plants where they're the
  //     only LM, their checked suppliers where the plant is shared);
  //   • a Regional Manager / other submitter is picked → suppliers at
  //     that person's plants.
  // Non-Directors stay limited to their own plants as before.
  const supplierChoices = useMemo(() => {
    const up = (v) => String(v || '').toUpperCase().trim();
    let base = (suppliers || []).filter((s) => s.SupplierCode);
    if (!isDirector) {
      base = base.filter((s) =>
        (s.plantAssignments || []).some((pa) => myPlantCodesSet.has(up(pa.plantCode))),
      );
    }
    const member = findSubmitterMember(team, submitterFilter);
    const lmScope = member ? resolveSupplierScope(member, team) : null;
    const plantList = plantFilter
      ? [up(plantFilter)]
      : plantScope
        ? [...plantScope].map(up)
        : null;
    if (plantList) {
      return base.filter((s) =>
        plantList.some(
          (pc) =>
            (s.plantAssignments || []).some((pa) => up(pa.plantCode) === pc) &&
            scopeAllowsSupplierAtPlant(lmScope, pc, s.SupplierCode),
        ),
      );
    }
    if (lmScope) {
      const codes = scopedSupplierCodes(lmScope, suppliers);
      return base.filter((s) => codes.has(up(s.SupplierCode)));
    }
    return base;
  }, [suppliers, isDirector, myPlantCodesSet, team, submitterFilter, plantFilter, plantScope]);

  // If the picked supplier drops out of the narrowed list (e.g. a different
  // plant was chosen), go back to "All suppliers".
  useEffect(() => {
    if (!supplierFilter) return;
    const still = supplierChoices.some(
      (s) => String(s.SupplierCode) === String(supplierFilter),
    );
    if (!still) setSupplierFilter('');
  }, [supplierChoices, supplierFilter]);

  // Supplier-code → display-name map so the grid can show full names
  // instead of just the short codes. Built once per load and handed to
  // each approval card, avoiding a per-row linear scan.
  const supplierNameByCode = useMemo(() => {
    const m = new Map();
    for (const s of suppliers) {
      if (s.SupplierCode) m.set(s.SupplierCode, s.Title || s.SupplierCode);
    }
    return m;
  }, [suppliers]);

  // "SUPPLIERCODE|PLANTCODE" keys for every plant assignment whose Coverage
  // Unit is "Per Week" (plantAssignment.hoursUnit). Rows matching one of
  // these pairs get a light-orange highlight so the approver can spot
  // per-week coverage at a glance — mirrors the submitter's tracker (per
  // Jillian, Aug 2026). Keyed per (supplier, plant) because a supplier can
  // be Per Week at one plant and Per Day at another.
  // Coverage can change over time (Oct 2026), so "bills weekly" is judged
  // for a specific tracker week: has(key, dates) uses the unit in effect
  // on that week's Monday (today's when no dates are given).
  const weeklyCoveragePairs = useMemo(() => {
    const byPair = new Map();
    for (const sup of suppliers) {
      const code = String(sup.SupplierCode || '').trim().toUpperCase();
      if (!code) continue;
      for (const pa of sup.plantAssignments || []) {
        const key = `${code}|${String(pa.plantCode || '').trim().toUpperCase()}`;
        if (!byPair.has(key)) byPair.set(key, []);
        byPair.get(key).push(pa);
      }
    }
    return {
      has(key, dates) {
        const list = byPair.get(key);
        if (!list) return false;
        const monday = Array.isArray(dates) ? dates[1] : null;
        return list.some(
          (pa) =>
            String(coverageOn(pa, monday || new Date())?.hoursUnit || '')
              .trim()
              .toLowerCase() === 'per week',
        );
      },
    };
  }, [suppliers]);

  // Supplier-code → array of part display names, so each approval card
  // can print a small "context" line at the bottom listing what the
  // supplier actually ships. Helps the approver sanity-check whether
  // the logged hours line up with the supplier's scope.
  const partsBySupplierCode = useMemo(() => {
    const m = new Map();
    for (const p of parts) {
      const code = p.SupplierCode;
      if (!code) continue;
      const label = (p.Title || '').trim() || p.PartNumber || 'Unnamed part';
      if (!m.has(code)) m.set(code, []);
      m.get(code).push(label);
    }
    // Sort each supplier's part list alphabetically so the comma-
    // separated line reads predictably across cards.
    for (const list of m.values()) list.sort((a, b) => a.localeCompare(b));
    return m;
  }, [parts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setActionError('');
    try {
      const [
        rows,
        approvedRows,
        rejectedRows,
        teamRows,
        supplierRows,
        partRows,
        plantRows,
      ] = await Promise.all([
        listTimesheetsByStatus(STATUS.PENDING),
        listTimesheetsByStatus(STATUS.APPROVED),
        listTimesheetsByStatus(STATUS.REJECTED),
        listTeam(),
        listSuppliers(),
        listParts(),
        listPlants(),
      ]);
      setPending(rows || []);
      setApprovedList(approvedRows || []);
      setRejectedList(rejectedRows || []);
      setTeam(teamRows || []);
      setSuppliers(supplierRows || []);
      setParts(partRows || []);
      setPlants(plantRows || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Deep-link handler: when the page is loaded with `?ts=<id>` (e.g.
  // from the Billing dashboard's date column), find the matching
  // timesheet across all three lists, expand its parent week group,
  // expand the individual card if it's in the Approved or Rejected
  // history (Pending cards are always shown expanded inside their
  // week), and scroll the card into view. Effect runs whenever the
  // lists update so it survives a refetch — but the search-params
  // dependency ensures we only re-target when the URL itself changes.
  const [searchParams] = useSearchParams();
  const focusTimesheetId = searchParams.get('ts') || '';
  useEffect(() => {
    if (!focusTimesheetId) return;
    const allLists = [
      ['pending', pending],
      ['rejected', rejectedList],
      ['approved', approvedList],
    ];
    let match = null;
    for (const [section, list] of allLists) {
      const hit = (list || []).find(
        (t) => String(t.id) === String(focusTimesheetId),
      );
      if (hit) {
        match = { section, timesheet: hit };
        break;
      }
    }
    if (!match) return;
    const rm = (match.timesheet.RegionalManager || '').trim() || 'Unassigned';
    const ws = match.timesheet.WeekStart || '';
    const weekKey = `${match.section}|${rm}|${ws}`;
    // Expand the week group (Pending defaults open, Approved /
    // Rejected default closed; the toggle set is XOR-against-default
    // so adding the key opens both cases without us having to know
    // which default is in play).
    setToggledWeeks((prev) => {
      if (match.section === 'pending') return prev; // already open by default
      const next = new Set(prev);
      next.add(weekKey);
      return next;
    });
    // For Approved / Rejected, also expand the individual card.
    if (match.section !== 'pending') {
      setExpandedApproved((prev) => {
        const next = new Set(prev);
        next.add(match.timesheet.id);
        return next;
      });
    }
    // Scroll into view after a tick so the DOM has time to render
    // the newly-expanded card. `?` because the element may not be
    // mounted on a refresh that beat the data fetch.
    setTimeout(() => {
      const el = document.getElementById(`timesheet-${match.timesheet.id}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }, [focusTimesheetId, pending, approvedList, rejectedList]);

  // Filter to the subset this viewer is allowed to approve. Directors
  // see everything; RMs see rows where the submitter's stored
  // RegionalManager matches their display name (case-insensitive),
  // PLUS their own submissions (an RM has no RM above them, so their
  // own rows carry no RegionalManager — per Jillian, July 2026, they
  // approve those themselves).
  // NOTE: the date-range picker is deliberately NOT applied to the
  // pending inbox (July 2026 fix). Pending entries need action no
  // matter how old the week is, and hiding them behind a "This Month"
  // default made the tab badge (which counts all pending in scope)
  // disagree with an apparently empty list. The explicit Plant /
  // Supplier / RM / Submitter dropdowns still apply; the date range
  // keeps governing the Approved and Rejected histories below.
  const inbox = useMemo(() => {
    // The self-approved (LM) view shows no Pending section at all.
    if (selfApprovedView) return [];
    if (!pending) return [];
    const scoped = isDirector
      ? pending
      : isRegionalManager
        ? pending.filter(
            (t) =>
              (t.RegionalManager || '').trim().toLowerCase() === normName ||
              isOwnSubmission(t),
          )
        : [];
    return scoped
      .filter(matchesRowFilters)
      .filter(matchesRm)
      .filter(matchesSubmitter);
  }, [
    selfApprovedView,
    pending,
    isDirector,
    isRegionalManager,
    normName,
    isOwnSubmission,
    matchesRowFilters,
    matchesRm,
    matchesSubmitter,
  ]);

  // Same filter applied to the Approved history so the audit trail on
  // this page only shows what the viewer is responsible for. Sorted
  // newest-approved-first so recently-approved rows are the first
  // thing the RM sees in the history list.
  const approvedInbox = useMemo(() => {
    if (!approvedList) return [];
    // Self-approved (LM) view: only the viewer's OWN approved trackers,
    // regardless of RM scoping. Otherwise the normal role scoping.
    const scoped = selfApprovedView
      ? approvedList.filter((t) => isOwnSubmission(t))
      : isDirector
        ? approvedList
        : isRegionalManager
          ? approvedList.filter(
              (t) =>
                (t.RegionalManager || '').trim().toLowerCase() === normName ||
                isOwnSubmission(t),
            )
          : [];
    return [...scoped]
      .filter(inRange)
      .filter(matchesRowFilters)
      .filter(matchesRm)
      .filter(matchesSubmitter)
      .sort((a, b) => {
        const da = new Date(a.ApprovedAt || a.WeekStart || 0).getTime();
        const db = new Date(b.ApprovedAt || b.WeekStart || 0).getTime();
        return db - da;
      });
  }, [
    selfApprovedView,
    approvedList,
    isDirector,
    isRegionalManager,
    normName,
    isOwnSubmission,
    inRange,
    matchesRowFilters,
    matchesRm,
    matchesSubmitter,
  ]);

  // Billing totals for the "Totals" tiles under the filters — mirror the
  // Billing Manager block, scoped to APPROVED (billable) trackers in the
  // current plant/supplier/date filters. When a plant or supplier filter
  // is active we trim each tracker's rows so the totals reflect only the
  // filtered work (same approach as the Billing Manager page).
  //
  // Scoped to the VIEWER first (per Jillian, Sep 2026): a Regional Manager
  // only sees totals for their own team's trackers, the same rule the
  // Pending / Approved / Rejected lists use. Previously this started from
  // the full approved list, so an RM with no submissions still saw the
  // whole company's hours and dollars. The Regional Manager / Submitted
  // By dropdowns narrow the totals too, so the tiles always describe the
  // trackers listed beneath them.
  const billingInput = useMemo(() => {
    const plantTarget = plantFilter ? plantFilter.toUpperCase() : '';
    const supplierTarget = supplierFilter ? supplierFilter.toUpperCase() : '';
    const rowBased = Boolean(plantTarget || supplierTarget);
    const list = (approvedList || [])
      .filter(inViewerScope)
      .filter(matchesRm)
      .filter(matchesSubmitter);
    if (!rowBased) return list;
    const out = [];
    for (const t of list) {
      const rows = Array.isArray(t.rows) ? t.rows : [];
      if (rows.length === 0) continue;
      const matching = rows.filter((r) => {
        const rP = String(r.plantCode || '').trim().toUpperCase();
        const rS = String(r.supplierCode || '').trim().toUpperCase();
        if (plantTarget && rP !== plantTarget) return false;
        if (supplierTarget && rS !== supplierTarget) return false;
        return true;
      });
      if (matching.length === 0) continue;
      out.push({ ...t, rows: matching });
    }
    return out;
  }, [
    approvedList,
    inViewerScope,
    matchesRm,
    matchesSubmitter,
    plantFilter,
    supplierFilter,
  ]);
  const billing = useMemo(
    () =>
      aggregateBilling(billingInput, suppliers, { startDate, endDate }, team),
    [billingInput, suppliers, startDate, endDate, team],
  );

  // Liaison Managers' "Your Approved Trackers" view opens each card to
  // its weekly breakdown by default (per Jillian, Aug 2026). We seed
  // expandedApproved with each approved id the first time it loads, so
  // the cards start open but a manager can still collapse one and it
  // stays collapsed (the ref remembers what we've already auto-opened).
  const autoExpandedApprovedRef = useRef(new Set());
  useEffect(() => {
    if (!selfApprovedView) return;
    const fresh = approvedInbox
      .map((t) => t.id)
      .filter((id) => id && !autoExpandedApprovedRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => autoExpandedApprovedRef.current.add(id));
    setExpandedApproved((prev) => {
      const next = new Set(prev);
      fresh.forEach((id) => next.add(id));
      return next;
    });
  }, [selfApprovedView, approvedInbox]);

  // Rejected = "Revisions Requested": the RM has bounced the
  // timesheet back to the submitter with a note. Same scoping rule
  // as the other lists; sorted newest-rejected-first so freshly
  // bounced rows are easy to find.
  const rejectedInbox = useMemo(() => {
    // The self-approved (LM) view shows no Rejected section.
    if (selfApprovedView) return [];
    if (!rejectedList) return [];
    const scoped = isDirector
      ? rejectedList
      : isRegionalManager
        ? rejectedList.filter(
            (t) =>
              (t.RegionalManager || '').trim().toLowerCase() === normName ||
              isOwnSubmission(t),
          )
        : [];
    return [...scoped]
      .filter(inRange)
      .filter(matchesRowFilters)
      .filter(matchesRm)
      .filter(matchesSubmitter)
      .sort((a, b) => {
        const da = new Date(a.RejectedAt || a.WeekStart || 0).getTime();
        const db = new Date(b.RejectedAt || b.WeekStart || 0).getTime();
        return db - da;
      });
  }, [
    selfApprovedView,
    rejectedList,
    isDirector,
    isRegionalManager,
    normName,
    isOwnSubmission,
    inRange,
    matchesRowFilters,
    matchesRm,
    matchesSubmitter,
  ]);

  const toggleApproved = (id) =>
    setExpandedApproved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Group a list of timesheets by RegionalManager so the page reads as
  // one section per RM. Returns [[rmName, [timesheet, ...]], ...]
  // sorted alphabetically by RM, with "Unassigned" pinned to the
  // bottom (rare — would mean a submitter without an RM on their
  // team record). Within each group the input order is preserved
  // (callers sort upstream so e.g. Approved stays newest-first).
  const groupByRm = (list) => {
    // Grouping key depends on who's viewing (Jillian, Aug 2026):
    //   • Directors oversee multiple RMs → group by Regional Manager.
    //   • A Regional Manager is already scoped to their own team → group
    //     by the SUBMITTER (Liaison Manager) so they scan by person.
    // The group header just shows this key string, so no other change is
    // needed. (Named groupByRm for its callers; the key now varies.)
    const byLiaisonManager = !isDirector;
    const FALLBACK = byLiaisonManager ? 'Unknown submitter' : 'Unassigned';
    const groups = new Map();
    for (const t of list) {
      const key = byLiaisonManager
        ? (t.SubmitterName || '').trim() || FALLBACK
        : (t.RegionalManager || '').trim() || FALLBACK;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    // Directors: keep each submitter's trackers together inside an RM's
    // group, alphabetically by submitter (Oct 2026). The sort is stable,
    // so each person's trackers keep their incoming order (e.g. newest
    // first on Approved).
    if (!byLiaisonManager) {
      for (const items of groups.values()) {
        items.sort((x, y) =>
          (x.SubmitterName || '').trim().localeCompare((y.SubmitterName || '').trim()),
        );
      }
    }
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === FALLBACK) return 1;
      if (b[0] === FALLBACK) return -1;
      return a[0].localeCompare(b[0]);
    });
  };
  const pendingGroups = groupByRm(inbox);
  const approvedGroups = groupByRm(approvedInbox);
  const rejectedGroups = groupByRm(rejectedInbox);

  // Within an RM group, bucket the cards by WeekStart so each week
  // becomes its own collapsible block. Returns
  // [[weekStart, [timesheet, ...]], ...] sorted newest-week-first
  // so the most-recent submissions land at the top of an RM's list.
  const groupByWeek = (list) => {
    const map = new Map();
    for (const t of list) {
      const ws = t.WeekStart || '';
      if (!map.has(ws)) map.set(ws, []);
      map.get(ws).push(t);
    }
    return Array.from(map.entries()).sort((a, b) =>
      String(b[0]).localeCompare(String(a[0])),
    );
  };

  // Bucket a list of timesheets by plant. A timesheet that touches
  // multiple plants (e.g. row 1 = SHAP, row 2 = OAC) appears in
  // EVERY plant's bucket — the Approve action is on the timesheet
  // record itself (single record), so clicking Approve on the SHAP
  // view and the OAC view both target the same row. The duplicate
  // display gives each plant lead full visibility into the work
  // logged at their plant.
  //
  // When a plant filter is active, suppress every other plant
  // bucket — by the time a timesheet reaches groupByPlant it has
  // already passed the plant filter, so we know it touches the filter
  // plant. Bucketing it under its OTHER plants would read like the
  // filter isn't applying ("I picked SHAP, why is OAC showing?").
  //
  // Credits (and any other rowless adjustments) drop into a
  // "(Credits)" pseudo-bucket within the RM so they stay visible
  // without forcing a parent-timesheet lookup. Plant order is
  // alphabetical; the credits bucket pins to the bottom.
  // Returns [[plantCode, [timesheet, ...]], ...].
  const groupByPlant = (list) => {
    // UNIFIED VIEW (Aug 2026, Jillian): the approval list no longer splits a
    // submitted tracker apart by plant. Each tracker now appears ONCE as its
    // full weekly grid (all plants/suppliers as rows), grouped only by
    // RM → week — matching the layout the submitter sees. This is a
    // passthrough so the three render sites don't have to change: it returns
    // a SINGLE bucket (empty plant code) holding every tracker exactly once
    // (dedup by id defensively). The plant dropdown still filters the list
    // upstream; it just no longer fragments each tracker. PlantGroup renders
    // this empty-code bucket bare (no plant chip / collapse).
    const seen = new Set();
    const unique = [];
    for (const t of list || []) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      unique.push(t);
    }
    return [['', unique]];
  };

  // Count of unique timesheets across a list (so the RM header's
  // "N timesheets" doesn't double-count cross-plant rows that
  // appear under multiple plants).
  const countUniqueTimesheets = (list) => {
    const seen = new Set();
    for (const t of list) seen.add(t.id);
    return seen.size;
  };

  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load Approvals</div>
        <p>{error.message || 'Unknown error.'}</p>
        <button className="btn btn-outline btn-sm" onClick={load}>
          Retry
        </button>
      </div>
    );
  }
  // Access: the normal Approvals page is Director / RM only. The
  // self-approved view is additionally open to Liaison Managers (it only
  // ever shows their OWN approved trackers, read-only).
  const allowedHere =
    isDirector ||
    isRegionalManager ||
    (selfApprovedView && isLiaisonManager);
  if (!allowedHere) {
    return (
      <div className="card">
        <div className="card-title">
          {selfApprovedView ? 'Approved Trackers' : 'Billing Tracker Approvals'}
        </div>
        <p className="muted">
          {selfApprovedView
            ? 'This view is for Liaison Managers to see their own approved Billing Trackers.'
            : 'Only Regional Managers and Directors can approve Billing Tracker entries.'}
        </p>
      </div>
    );
  }

  const onApprove = async (t) => {
    if (acting) return;
    setActing(t.id);
    setActionError('');
    try {
      await approveTimesheet(t.id, name || email || '');
      await load();
    } catch (err) {
      setActionError(err.message || 'Approve failed.');
    } finally {
      setActing(null);
    }
  };

  const onReject = async (t) => {
    if (acting) return;
    // A quick prompt keeps the UI simple — a full rejection form is
    // overkill for a one-sentence note. Cancel bails out cleanly.
    const reason = window.prompt(
      `Reject ${t.SubmitterName || 'this submitter'}'s ${t.WeekStart} Billing Tracker entry?\n\nTell them what to fix:`,
      '',
    );
    if (reason === null) return; // user hit Cancel
    setActing(t.id);
    setActionError('');
    try {
      await rejectTimesheet(t.id, name || email || '', reason || '');
      await load();
    } catch (err) {
      setActionError(err.message || 'Reject failed.');
    } finally {
      setActing(null);
    }
  };

  // The over-40-hrs OT approval mechanism was removed (Jillian, Aug 2026).
  // Hours over 40 now bill normally; OT is captured only via the dedicated
  // "OT Liaison Hours" coverage card (billed at 1.5x).

  return (
    <>
      <div className="page-header">
        <h1>{selfApprovedView ? 'Approved Trackers' : 'Billing Tracker Approvals'}</h1>
        <div className="actions">
          <button className="btn btn-outline" onClick={load}>
            Refresh
          </button>
        </div>
      </div>
      {selfApprovedView && (
        <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
          Your approved Billing Trackers. Use the filters to find a specific
          week, plant, or supplier.
        </p>
      )}

      {/* Date-range filter — same pattern as the Billing dashboard
          (quick presets on top, explicit From/To pickers below). The
          range filters Pending, Approved (history), and Rejected
          (revisions requested) at once so the page reads as a single
          cohesive window into a chosen period. Filter applies to
          WeekStart. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          {/* Preset order matches the Billing page for consistency:
              lead with the default (This Month), then current →
              previous within each pair, scaling from shorter to
              longer windows. */}
          <PresetButton preset="this-month" active={activePreset} onClick={applyPreset}>
            This Month
          </PresetButton>
          <PresetButton preset="last-month" active={activePreset} onClick={applyPreset}>
            Last Month
          </PresetButton>
          <PresetButton preset="this-week" active={activePreset} onClick={applyPreset}>
            This Week
          </PresetButton>
          <PresetButton preset="last-week" active={activePreset} onClick={applyPreset}>
            Last Week
          </PresetButton>
          <PresetButton preset="past-4-weeks" active={activePreset} onClick={applyPreset}>
            Past 4 Weeks
          </PresetButton>
          <PresetButton preset="past-5-weeks" active={activePreset} onClick={applyPreset}>
            Past 5 Weeks
          </PresetButton>
          <PresetButton preset="ytd" active={activePreset} onClick={applyPreset}>
            Year to Date
          </PresetButton>
          <PresetButton preset="last-year" active={activePreset} onClick={applyPreset}>
            Last Year
          </PresetButton>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              From
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActivePreset(null);
              }}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              To
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActivePreset(null);
              }}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
              }}
            />
          </div>
          <div
            className="muted"
            style={{ marginLeft: 'auto', fontSize: 13, alignSelf: 'center' }}
          >
            {formatApprovalsRangeLabel(startDate, endDate)}
          </div>
        </div>

        {/* Plant + Supplier filters — sit on their own row under the
            date pickers so they don't fight for horizontal space on
            narrow screens. Both default to "All …" (no filter); they
            stack with the date range, so a director can scope down
            to a specific (plant × supplier × period) slice. Supplier
            options come from the full loaded suppliers list, not
            narrowed by the current plant filter, so a supplier that
            only covers one plant is still findable from any plant
            view. */}
        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            marginTop: 12,
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
            paddingTop: 12,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Plant
            </label>
            <select
              value={plantFilter}
              onChange={(e) => setPlantFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 240,
              }}
            >
              <option value="">All plants</option>
              {plantChoices
                .filter(
                  (p) =>
                    !plantScope ||
                    plantScope.has(String(p.PlantCode).toUpperCase()),
                )
                .slice()
                .sort((a, b) =>
                  String(a.PlantCode).localeCompare(String(b.PlantCode)),
                )
                .map((p) => (
                  <option key={p.PlantCode} value={p.PlantCode}>
                    {p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Supplier
            </label>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 240,
              }}
            >
              <option value="">All suppliers</option>
              {supplierChoices
                .slice()
                .sort((a, b) => {
                  // Sort by supplier name (Title) alphabetically so the
                  // dropdown reads like the rest of the Suppliers page.
                  const an = (a.Title || a.SupplierCode || '').toLowerCase();
                  const bn = (b.Title || b.SupplierCode || '').toLowerCase();
                  return an.localeCompare(bn);
                })
                .map((s) => (
                  <option key={s.SupplierCode} value={s.SupplierCode}>
                    {s.Title
                      ? `${s.SupplierCode} — ${s.Title}`
                      : s.SupplierCode}
                  </option>
                ))}
            </select>
          </div>
          {/* Billing Type filter — scopes the grid to a single coverage
              type (Standard / OT / Mileage / Expenses) across all roles
              (per Jillian, Aug 2026). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Billing Type
            </label>
            <select
              value={coverageFilter}
              onChange={(e) => setCoverageFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 200,
              }}
            >
              <option value="">All billing types</option>
              <option value="Standard">Standard Liaison Hours</option>
              <option value="Overage">OT Liaison Hours</option>
              <option value="Mileage">Mileage</option>
              <option value="Expenses">Expenses</option>
            </select>
          </div>
          {/* Overages Only (Nov 2026) — Standard hours billed over the
              supplier's standard coverage, i.e. the cells that turn red. */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              cursor: 'pointer',
              alignSelf: 'flex-end',
              paddingBottom: 8,
            }}
            title="Show only suppliers billed over their standard coverage (the red hours)"
          >
            <input
              type="checkbox"
              checked={overagesOnly}
              onChange={(e) => setOveragesOnly(e.target.checked)}
            />
            <span>Overages Only</span>
          </label>
          {/* Regional Manager + Submitted By are hidden in the LM
              self-view — for an LM those are always their own RM and
              themselves (per Jillian, July 2026). */}
          {!selfApprovedView && (
          <>
          {/* Regional Manager filter is Director-only — an RM already sees
              just their own team, so the RM dropdown is noise for them
              (Jillian, Aug 2026). Directors keep it to filter across RMs. */}
          {isDirector && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Regional Manager
            </label>
            <select
              value={rmFilter}
              onChange={(e) => setRmFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 220,
              }}
            >
              <option value="">All Regional Managers</option>
              {rmOptions.map((rm) => (
                <option key={rm} value={rm}>
                  {rm}
                </option>
              ))}
            </select>
          </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--brand-muted)',
              }}
            >
              Submitted By
            </label>
            <select
              value={submitterFilter}
              onChange={(e) => setSubmitterFilter(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--brand-border)',
                borderRadius: 6,
                fontSize: 14,
                minWidth: 220,
              }}
            >
              <option value="">All submitters</option>
              {submitterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          </>
          )}
          {(plantFilter ||
            supplierFilter ||
            coverageFilter ||
            overagesOnly ||
            rmFilter ||
            submitterFilter) && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                setPlantFilter('');
                setSupplierFilter('');
                setCoverageFilter('');
                setOveragesOnly(false);
                setRmFilter('');
                setSubmitterFilter('');
              }}
              style={{ alignSelf: 'flex-end', marginBottom: 1 }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Totals — mirrors the Billing Manager "Totals" block, scoped to
          approved (billable) trackers in the current filters. Hidden on
          the Liaison Manager self-approved view. */}
      {!selfApprovedView && (
        <div style={{ marginTop: 20, marginBottom: 8 }}>
          <h2
            style={{
              fontSize: 14,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'var(--brand-muted)',
              margin: '0 0 10px',
            }}
          >
            Totals
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 14,
            }}
          >
            <TotalTile
              label="Plants Total"
              value={`${countUniquePlants(billing)}`}
              // Denominator is the viewer's plant scope (Directors: every
              // plant; RMs: the plants their team covers), so an RM sees
              // "3 of 6", not "3 of 74".
              detail={`of ${plantChoices.length} plants`}
            />
            <TotalTile
              label="Hours Total"
              value={fmtNum(billing.grandTotalHours)}
              unit="hrs"
              detail={hoursTotalDetail(billing)}
            />
            <TotalTile
              label="Expenses Total"
              value={fmtMoney(billing.grandTotalExpenseDollars)}
            />
            <TotalTile
              label="Mileage Total"
              value={fmtMoney(billing.grandTotalMileageDollars)}
              detail={
                billing.grandTotalMiles > 0
                  ? `${fmtNum(billing.grandTotalMiles)} mi`
                  : undefined
              }
            />
            <TotalTile
              label="Billing Total"
              value={fmtMoney(billing.grandTotal)}
              detail={periodTotalDetail(billing)}
              highlighted
            />
          </div>
        </div>
      )}

      {actionError && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 6,
            background: 'rgba(220, 38, 38, 0.08)',
            color: 'var(--brand-danger)',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {actionError}
        </div>
      )}

      {/* Pending + Rejected are hidden in the Liaison Manager
          "Approved Trackers" self-view — only the Approved history
          below renders there. */}
      {!selfApprovedView && (
        <>
      <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Pending Approval</h2>
      {pendingGroups.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ marginTop: 0 }}>
            {isDirector
              ? "No Billing Tracker entries are pending approval right now. When team members submit one, it'll appear here."
              : "None of your direct reports have pending Billing Tracker entries. When they submit one, it'll appear here."}
          </p>
        </div>
      ) : (
        // Three-level grouping: RM → Plant → Week → cards. Plants and
        // Weeks both default collapsed so the page lands compact —
        // an RM scans the plant headers within their queue, expands
        // the plants they care about, then expands a week to see the
        // cards. Multi-plant timesheets appear once under each plant
        // they touch (Approve still happens once on the underlying
        // record). The RM count above each section is deduped so
        // cross-plant timesheets aren't double-counted.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {pendingGroups.map(([rm, list]) => {
            const plantGroups = groupByPlant(list);
            return (
              <div key={`pending-${rm}`}>
                <RmGroupHeader
                  rm={rm}
                  count={countUniqueTimesheets(list)}
                />
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {plantGroups.map(([plant, plantList]) => {
                    const plantKey = `pending|${rm}|${plant}`;
                    const plantExpanded = isPlantExpanded(plantKey, false);
                    // Trackers list flat (no week buckets) — each carries
                    // its own date header via TrackerDateHeader.
                    return (
                      <PlantGroup
                        key={plantKey}
                        plantCode={plant}
                        count={plantList.length}
                        expanded={plantExpanded}
                        onToggle={() => togglePlant(plantKey)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 14,
                          }}
                        >
                          {[...plantList]
                            .sort((a, b) =>
                              String(b.WeekStart || '').localeCompare(
                                String(a.WeekStart || ''),
                              ),
                            )
                            .map((t) => (
                              <div
                                key={`${plant}-${t.id}`}
                                id={`timesheet-${t.id}`}
                              >
                                <TrackerDateHeader timesheet={t} showSubmitter={isDirector} />
                                <ApprovalCard
                                  timesheet={t}
                                  supplierNameByCode={supplierNameByCode}
                                  weeklyCoveragePairs={weeklyCoveragePairs}
                                  plants={plants}
                                  suppliers={suppliers}
                                  plantFilter={plantFilter}
                                  supplierFilter={supplierFilter}
                                  coverageFilter={coverageFilter}
                                  overagesOnly={overagesOnly}
                                  partsBySupplierCode={partsBySupplierCode}
                                  acting={acting === t.id}
                                  onApprove={() => onApprove(t)}
                                  onReject={() => onReject(t)}
                                  // Edit opens the full submitter editor
                                  // pre-loaded with this timesheet via the
                                  // Submit tab.
                                  onEdit={() =>
                                    navigate(
                                      `/timesheets/submit?edit=${encodeURIComponent(t.id)}`,
                                    )
                                  }
                                />
                              </div>
                            ))}
                        </div>
                      </PlantGroup>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Rejected — bounced back to the submitter with a note from
          the RM. Sits between Pending and Approved because the
          submitter is the one with action to take next, but the RM
          (and Directors) need visibility on outstanding revision
          requests so nothing falls between the cracks. Same RM →
          Week → cards layout the other sections use; weeks default
          collapsed since this is a watch-list, not an action queue. */}
      <h2 style={{ margin: '24px 0 12px', fontSize: 16 }}>
        Rejected – Revisions Requested
      </h2>
      {rejectedGroups.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ marginTop: 0 }}>
            No Billing Tracker entries are awaiting revisions right now.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {rejectedGroups.map(([rm, list]) => {
            const plantGroups = groupByPlant(list);
            return (
              <div key={`rejected-${rm}`}>
                <RmGroupHeader
                  rm={rm}
                  count={countUniqueTimesheets(list)}
                />
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {plantGroups.map(([plant, plantList]) => {
                    const plantKey = `rejected|${rm}|${plant}`;
                    const plantExpanded = isPlantExpanded(plantKey, false);
                    // Trackers list flat (no week buckets) — each carries
                    // its own date header via TrackerDateHeader.
                    return (
                      <PlantGroup
                        key={plantKey}
                        plantCode={plant}
                        count={plantList.length}
                        expanded={plantExpanded}
                        onToggle={() => togglePlant(plantKey)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          {[...plantList]
                            .sort((a, b) =>
                              String(b.WeekStart || '').localeCompare(
                                String(a.WeekStart || ''),
                              ),
                            )
                            .map((t) => (
                              <div
                                key={`${plant}-${t.id}`}
                                id={`timesheet-${t.id}`}
                              >
                                <TrackerDateHeader timesheet={t} showSubmitter={isDirector} />
                                <RejectedCard
                                  timesheet={t}
                                  supplierNameByCode={supplierNameByCode}
                                  weeklyCoveragePairs={weeklyCoveragePairs}
                                  plants={plants}
                                  suppliers={suppliers}
                                  plantFilter={plantFilter}
                                  supplierFilter={supplierFilter}
                                  coverageFilter={coverageFilter}
                                  overagesOnly={overagesOnly}
                                  partsBySupplierCode={partsBySupplierCode}
                                  expanded={expandedApproved.has(t.id)}
                                  onToggle={() => toggleApproved(t.id)}
                                />
                              </div>
                            ))}
                        </div>
                      </PlantGroup>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

        </>
      )}

      {/* Approved history — clickable cards that expand to the same
          read-only grid the Pending cards show, but with no Approve /
          Reject buttons. Default collapsed so the page lands compact;
          the header line surfaces submitter, week, Date Submitted,
          and Date Approved at a glance. Grouped by RM the same way
          the Pending list above is, for a consistent scan order. */}
      <h2 style={{ margin: '24px 0 12px', fontSize: 16 }}>
        {selfApprovedView ? 'Your Approved Trackers' : 'Approved'}
      </h2>
      {approvedGroups.length === 0 ? (
        <div className="card">
          <p className="muted" style={{ marginTop: 0 }}>
            {selfApprovedView
              ? 'None of your Billing Trackers have been approved in this date range yet. Try widening the range above.'
              : "No approved Billing Tracker entries yet. Once you approve a submission, it'll move down here."}
          </p>
        </div>
      ) : (
        // Same three-level layout as Pending — RM → Plant → Week →
        // cards — but Approved plants and weeks default *closed*
        // since the page is a history view, not an action queue.
        // The RM scans the section, drills into a plant, then a
        // week to see specific submissions.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {approvedGroups.map(([rm, list]) => {
            const plantGroups = groupByPlant(list);
            return (
              <div key={`approved-${rm}`}>
                <RmGroupHeader
                  rm={rm}
                  count={countUniqueTimesheets(list)}
                />
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {plantGroups.map(([plant, plantList]) => {
                    const plantKey = `approved|${rm}|${plant}`;
                    // Liaison Managers' "Your Approved Trackers" opens each
                    // plant group by default so the weekly trackers show
                    // expanded without a click — matching the card
                    // auto-expand above (per Jillian, Aug 2026). The
                    // approver inbox (RM / Director) stays collapsed.
                    const plantExpanded = isPlantExpanded(
                      plantKey,
                      selfApprovedView,
                    );
                    // Trackers list flat (no week buckets) — each carries
                    // its own date header via TrackerDateHeader.
                    return (
                      <PlantGroup
                        key={plantKey}
                        plantCode={plant}
                        count={plantList.length}
                        expanded={plantExpanded}
                        onToggle={() => togglePlant(plantKey)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                          }}
                        >
                          {[...plantList]
                            .sort((a, b) =>
                              String(b.WeekStart || '').localeCompare(
                                String(a.WeekStart || ''),
                              ),
                            )
                            .map((t) => (
                              <div
                                key={`${plant}-${t.id}`}
                                id={`timesheet-${t.id}`}
                              >
                                <TrackerDateHeader timesheet={t} showSubmitter={isDirector} />
                                <ApprovedCard
                                  timesheet={t}
                                  supplierNameByCode={supplierNameByCode}
                                  weeklyCoveragePairs={weeklyCoveragePairs}
                                  plants={plants}
                                  suppliers={suppliers}
                                  plantFilter={plantFilter}
                                  supplierFilter={supplierFilter}
                                  coverageFilter={coverageFilter}
                                  overagesOnly={overagesOnly}
                                  partsBySupplierCode={partsBySupplierCode}
                                  expanded={expandedApproved.has(t.id)}
                                  onToggle={() => toggleApproved(t.id)}
                                  trackerOnly={!selfApprovedView}
                                  // Directors can retract an approved
                                  // tracker, edit it and save over it
                                  // (Nov 2026). Credits have no grid.
                                  onRetract={
                                    isDirector && !selfApprovedView && !t.IsCredit
                                      ? () =>
                                          navigate(
                                            `/timesheets/submit?edit=${encodeURIComponent(t.id)}`,
                                          )
                                      : undefined
                                  }
                                />
                              </div>
                            ))}
                        </div>
                      </PlantGroup>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---- Approval card --------------------------------------------------

/**
 * Read-only summary of a single pending timesheet plus Approve / Reject
 * buttons. Shows the same grid layout the submitter filled out, with
 * totals at the bottom, so the RM can verify before approving.
 */
function ApprovalCard({
  timesheet,
  supplierNameByCode,
  weeklyCoveragePairs,
  partsBySupplierCode,
  plants = [],
  suppliers = [],
  // Active plant / supplier filters. When set, the grid shows ONLY the
  // line items matching them (not the whole weekly tracker) and the totals
  // recompute from those visible rows (per Jillian, Aug 2026).
  plantFilter = '',
  supplierFilter = '',
  coverageFilter = '',
  overagesOnly = false,
  acting,
  onApprove,
  onReject,
  // Optional — when provided, an "Edit" button appears next to
  // Reject and Approve. Clicking it lets the RM open the full
  // submitter editor pre-loaded with this timesheet's values. The
  // parent decides whether editing is allowed (currently only for
  // Pending rows).
  onEdit,
  // When true, the card renders without Approve / Reject buttons and
  // without its own outer "card" wrapper. Used by ApprovedCard to
  // reuse the same grid + totals layout inside an expandable wrapper.
  readOnly = false,
}) {
  // Zero-hour / untouched supplier rows are dropped at submit now (Sep
  // 2026); this strips them from trackers submitted before that change so
  // the Approvals view never shows a supplier with nothing billed.
  const rows = stripEmptyRows(timesheet.rows);
  const dates = weekDates(timesheet.WeekStart);

  // When a plant and/or supplier filter is active, show ONLY the line items
  // that match — not the whole weekly tracker. A row must match every
  // active filter (per Jillian, Aug 2026: "show individual line items vs.
  // entirely weekly Trackers").
  const filtering =
    Boolean(plantFilter) ||
    Boolean(supplierFilter) ||
    Boolean(coverageFilter) ||
    overagesOnly;
  const rowMatchesFilters = (r) => {
    if (
      plantFilter &&
      String(r.plantCode || '').trim().toUpperCase() !==
        plantFilter.trim().toUpperCase()
    ) {
      return false;
    }
    if (
      supplierFilter &&
      String(r.supplierCode || '').trim().toUpperCase() !==
        supplierFilter.trim().toUpperCase()
    ) {
      return false;
    }
    if (coverageFilter && String(r.coverageType || '') !== coverageFilter) {
      return false;
    }
    if (overagesOnly && !rowIsOverStandard(r, plants, suppliers, dates)) {
      return false;
    }
    return true;
  };
  const visibleRows = filtering ? rows.filter(rowMatchesFilters) : rows;

  // Does any visible row's (supplier, plant) bill weekly? Drives whether
  // the orange "Suppliers that Bill Weekly" legend key shows in the footer.
  const rowIsWeekly = (r) =>
    weeklyCoveragePairs &&
    r.supplierCode &&
    r.plantCode &&
    weeklyCoveragePairs.has(
      `${String(r.supplierCode).trim().toUpperCase()}|${String(r.plantCode)
        .trim()
        .toUpperCase()}`,
      dates,
    );
  const hasWeeklyRow = visibleRows.some(rowIsWeekly);

  // Group the grid rows into coverage-type sections (Standard → OT →
  // Mileage → Expenses → anything else) so the four categories read as
  // distinct blocks instead of blending together. An orange rule is drawn
  // between sections in the row map below (per Jillian, Aug 2026).
  const SECTION_ORDER = ['Standard', 'Overage', 'Mileage', 'Expenses'];
  const sectionOf = (r) => {
    const ct = r?.coverageType;
    return SECTION_ORDER.includes(ct) ? ct : 'Other';
  };
  const sectionRank = (r) => {
    const idx = SECTION_ORDER.indexOf(sectionOf(r));
    return idx === -1 ? SECTION_ORDER.length : idx;
  };
  // Stable ordering: sort by section rank, keeping original order within a
  // section (origIndex tiebreaker also guards older engines).
  const orderedRows = visibleRows
    .map((r, origIndex) => ({ r, origIndex }))
    .sort((a, b) => sectionRank(a.r) - sectionRank(b.r) || a.origIndex - b.origIndex);

  // Totals derived from the stored roll-ups (trust the service-side
  // computation rather than re-summing client-side).
  // Totals: use the stored roll-ups for the whole tracker, but when a
  // filter is active recompute from the visible rows so the totals match
  // what's shown. Hours count actual worked hours 1:1 — OT is no longer
  // inflated by 1.5× (the premium moved to the rate at billing time, per
  // Jillian, Aug 2026).
  const sumRowHours = (list) =>
    Math.round(
      list.reduce((acc, r) => {
        if (r.coverageType === 'Mileage' || r.coverageType === 'Expenses') {
          return acc;
        }
        const s = DAY_KEYS.reduce((a, k) => a + (Number(r.values?.[k]) || 0), 0);
        return acc + s;
      }, 0) * 100,
    ) / 100;
  const sumRowMiles = (list) =>
    list
      .filter((r) => r.coverageType === 'Mileage')
      .reduce(
        (acc, r) =>
          acc + DAY_KEYS.reduce((a, k) => a + (Number(r.values?.[k]) || 0), 0),
        0,
      );
  // Total Hours is the live BILLED hours of the rows currently shown (the
  // filtered set when a plant/supplier filter is active, otherwise the
  // whole tracker) — never the commission-weighted total, and never the
  // possibly-stale saved value. This keeps it reconciled with the "Billed
  // Hours per Day" grand below (per Jillian, Aug 2026).
  const totalHours = sumRowHours(visibleRows);
  const totalMiles = filtering
    ? sumRowMiles(visibleRows)
    : Number(timesheet.TotalMiles) || 0;
  const totalMileageDollars = filtering
    ? calcMileageDollars(totalMiles)
    : Number(timesheet.TotalMileageDollars) || calcMileageDollars(totalMiles);
  const totalExpenseDollars = (filtering ? visibleRows : rows)
    .filter((r) => r.coverageType === 'Expenses')
    .reduce((acc, r) => acc + (Number(r.expenseAmount) || 0), 0);

  // Card body — renders inside an outer wrapper. The "card" class is
  // applied here when this component is used standalone (Pending),
  // and skipped in readOnly mode so the parent (ApprovedCard) can
  // own the wrapping element.
  const Wrapper = readOnly ? 'div' : 'div';
  return (
    <Wrapper className={readOnly ? '' : 'card'}>
      {!readOnly && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 18,
                fontWeight: 700,
                flexWrap: 'wrap',
              }}
            >
              {timesheet.SubmitterName || 'Unknown submitter'}
              {/* Adjustment pill — Supplement (orange) for additions,
                  Credit (blue) for subtractions. Only one renders at
                  a time so the visual is unambiguous. */}
              {timesheet.IsSupplement && <SupplementPill />}
              {timesheet.IsCredit && <CreditPill />}
            </div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              Week of {formatWeekRange(dates)}
              {timesheet.SubmittedAt &&
                ` · Submitted on ${formatTimestamp(timesheet.SubmittedAt)}`}
            </div>
            {/* Adjustment footnote — Reason + Billing Disposition the
                creator picked, plus the credit amount on credits.
                Shown directly under the submitter line so the
                approver sees the "why" before scanning the grid. */}
            {timesheet.IsAdjustment && (
              <SupplementContext timesheet={timesheet} />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {onEdit && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={onEdit}
                disabled={acting}
                title="Open this Billing Tracker entry in the editor"
              >
                Edit
              </button>
            )}
            <button
              className="btn btn-outline"
              onClick={onReject}
              disabled={acting}
              style={{
                borderColor: 'var(--brand-danger)',
                color: 'var(--brand-danger)',
              }}
            >
              Reject
            </button>
            <button
              className="btn btn-primary"
              onClick={onApprove}
              disabled={acting}
            >
              {acting ? 'Working…' : 'Approve'}
            </button>
          </div>
        </div>
      )}

      {/* The over-40-hrs OT callout was removed (Jillian, Aug 2026).
          Hours over 40 now bill normally; OT is captured only via the
          dedicated "OT Liaison Hours" coverage card (billed at 1.5x). */}

      {/* Entry rows — read-only mirror of the submitter's grid. */}
      <div style={{ marginTop: 14, overflowX: 'auto' }}>
        <table className="mini-table" style={{ minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ width: 100 }}>Plant</th>
              <th style={{ width: 90 }}>Supplier</th>
              <th style={{ width: 180 }}>Supplier Name</th>
              <th style={{ width: 110 }}>Coverage Type</th>
              <th>Explanation</th>
              {dates.map((d, i) => (
                <th key={DAY_KEYS[i]} style={{ width: 60, textAlign: 'center' }}>
                  {d.toLocaleDateString(undefined, { weekday: 'short' })}
                  <div
                    className="muted"
                    style={{ fontSize: 10, fontWeight: 400 }}
                  >
                    {String(d.getMonth() + 1).padStart(2, '0')}/
                    {String(d.getDate()).padStart(2, '0')}
                  </div>
                </th>
              ))}
              <th style={{ width: 90, textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={13} className="muted">
                  {filtering
                    ? 'No line items match the current filters.'
                    : 'No entries.'}
                </td>
              </tr>
            )}
            {orderedRows.map(({ r, origIndex }, pos) => {
              const isMileage = r.coverageType === 'Mileage';
              const isExpense = r.coverageType === 'Expenses';
              // Over/under-standard day coloring — mirrors the submitter's
              // tracker so approvers see the same flags on Standard hours
              // (blue = under the standard, red = over) — per Jillian, Aug
              // 2026. Only computed for hour rows with a plant + supplier.
              const devs = standardHoursDeviations(r, plants, suppliers, dates);
              // OT Liaison Hours (coverage type 'Overage') show the ACTUAL
              // hours worked — no 1.5× on the hours. The time-and-a-half
              // premium is applied to the rate at billing time (per Jillian,
              // Aug 2026). This mirrors the submitter's tracker.
              const rowSum = DAY_KEYS.reduce(
                (acc, k) => acc + (Number(r.values?.[k]) || 0),
                0,
              );
              const rowTotalDisplay = isExpense
                ? `$${(Number(r.expenseAmount) || 0).toFixed(2)}`
                : isMileage
                  ? `$${calcMileageDollars(rowSum).toFixed(2)}`
                  : String(Math.round(rowSum * 100) / 100);
              // Light-orange highlight when this (supplier, plant) pair has
              // Coverage Unit = "Per Week" — mirrors the submitter's tracker
              // (per Jillian, Aug 2026).
              const billsWeekly =
                weeklyCoveragePairs &&
                r.supplierCode &&
                r.plantCode &&
                weeklyCoveragePairs.has(
                  `${String(r.supplierCode).trim().toUpperCase()}|${String(
                    r.plantCode,
                  )
                    .trim()
                    .toUpperCase()}`,
                  dates,
                );
              // Weekly-billing suppliers are flagged on the WEEK'S TOTAL,
              // not each day — so for a weekly Standard row we suppress
              // the per-day cue and color the Total cell instead (mirrors
              // the submitter's tracker, per Jillian, Aug 2026).
              const isWeeklyStd =
                billsWeekly && (r.coverageType || 'Standard') === 'Standard';
              const weeklyDev = isWeeklyStd
                ? weeklyStandardDeviation(r, plants, suppliers, dates)
                : null;
              const dayDevs = isWeeklyStd ? null : devs;
              const weeklyTotalColor = weeklyDev
                ? weeklyDev.dir === 'under'
                  ? STD_UNDER_COLOR
                  : STD_OVER_COLOR
                : undefined;
              // Draw an orange rule before the first row of each new
              // section (except the very first) so Standard / OT / Mileage
              // / Expenses read as separate blocks.
              const newSection =
                pos > 0 && sectionOf(r) !== sectionOf(orderedRows[pos - 1].r);
              return (
                <Fragment key={origIndex}>
                  {newSection && (
                    <tr aria-hidden="true">
                      <td
                        colSpan={13}
                        style={{
                          borderTop: '2px solid var(--brand-primary, #E87722)',
                          padding: 0,
                          height: 0,
                        }}
                      />
                    </tr>
                  )}
                  <tr
                    style={
                      billsWeekly ? { backgroundColor: '#fff7ed' } : undefined
                    }
                  >
                  <td>
                    {r.plantCode ? (
                      <Link
                        to={`/plants/${encodeURIComponent(r.plantCode)}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="badge plant">{r.plantCode}</span>
                      </Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{r.supplierCode || <span className="muted">—</span>}</td>
                  <td>
                    {r.supplierCode ? (
                      <span>
                        {supplierNameByCode?.get(r.supplierCode) ||
                          r.supplierCode}
                        {/* Service the hours belong to, when the row
                            names one other than the default — so an
                            approver can tell a Lineside Support line
                            from Standard Coverage at the same plant. */}
                        {r.service &&
                          String(r.service).trim().toLowerCase() !==
                            'standard coverage' && (
                            <span className="muted" style={{ marginLeft: 6 }}>
                              — {r.service}
                            </span>
                          )}
                        {/* Coverage hours on file, same gray caption the
                            submitter saw on the tracker (per Jillian, Oct
                            2026): "Coverage: 8 Hours / Week" or
                            "Coverage: On Call". Standard rows only. */}
                        {(() => {
                          if ((r.coverageType || 'Standard') !== 'Standard') return null;
                          const cov = coverageCaption(r, suppliers, dates);
                          if (!cov) return null;
                          return (
                            <div
                              className="muted"
                              style={{ fontSize: 11.5, marginTop: 3, whiteSpace: 'nowrap' }}
                              title="Coverage hours set on the Supplier page for this plant"
                            >
                              Coverage:{' '}
                              <span style={{ color: 'var(--brand-ink)', fontWeight: 600 }}>
                                {cov}
                              </span>
                            </div>
                          );
                        })()}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{COVERAGE_TYPE_LABELS[r.coverageType] || r.coverageType}</td>
                  <td>
                    {/* Per-day explanations (Sep 2026) list one line per
                        flagged day, "SU 09/20: ..."; older rows with a
                        single note render it as before. */}
                    {(() => {
                      const lines = explanationLines(r, timesheet.WeekStart);
                      if (lines.length === 0) return <span className="muted">—</span>;
                      if (lines.length === 1 && !lines[0].label) return lines[0].text;
                      return (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          {lines.map((l) => (
                            <li key={l.key} style={{ marginBottom: 2 }}>
                              <strong style={{ marginRight: 4 }}>{l.label}:</strong>
                              {l.text}
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                    {/* Paid To — Expense / Mileage rows carry the team
                        member being reimbursed (per Jillian, July
                        2026) so the approver knows who to pay. */}
                    {(isExpense || isMileage) && r.assignedTo && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--brand-ink)',
                        }}
                      >
                        Paid To: {r.assignedTo}
                      </div>
                    )}
                    {/* Receipt link — only shown for Expenses rows
                        that have an attached file. target=_blank so
                        the approval table stays put when the RM clicks
                        through to review the receipt. */}
                    {isExpense && r.receiptFileName && (
                      <div style={{ marginTop: 4 }}>
                        <a
                          href={r.receiptUrl || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 12,
                            color: 'var(--brand-primary, #E87722)',
                            wordBreak: 'break-all',
                          }}
                          title="Open receipt in a new window"
                        >
                          {r.receiptFileName}
                        </a>
                      </div>
                    )}
                  </td>
                  {isExpense
                    ? DAY_KEYS.map((k) => (
                        <td
                          key={k}
                          className="muted"
                          style={{ textAlign: 'center' }}
                        >
                          —
                        </td>
                      ))
                    : DAY_KEYS.map((k) => {
                        const dev = dayDevs && dayDevs[k];
                        const devColor = dev
                          ? dev.dir === 'under'
                            ? STD_UNDER_COLOR
                            : STD_OVER_COLOR
                          : undefined;
                        return (
                          <td
                            key={k}
                            style={{
                              textAlign: 'center',
                              color: devColor,
                              fontWeight: devColor ? 700 : undefined,
                            }}
                            title={
                              dev
                                ? `${dev.actual} hrs — ${dev.dir} the ${dev.expected} hr standard`
                                : undefined
                            }
                          >
                            {/* Show a real "0" for a deviation so an
                                under-standard zero renders in the deviation
                                color rather than a blank cell. */}
                            {Number(r.values?.[k]) || (dev ? 0 : '')}
                          </td>
                        );
                      })}
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: weeklyTotalColor ? 700 : 600,
                      color: weeklyTotalColor,
                    }}
                    title={
                      weeklyDev
                        ? `Weekly total ${weeklyDev.actual} hrs — ${weeklyDev.dir} the ${weeklyDev.expected} hr standard`
                        : undefined
                    }
                  >
                    {rowTotalDisplay}
                  </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
          {/* Billed + Commission hours per day — sums every hour row
              (Standard + OT; mileage / expenses aren't hours) so approvers
              see the same per-day roll-up the submitter sees at the bottom
              of each plant block. Billed counts OT 1:1; Commission counts
              OT at 1.5× (per Jillian, Aug 2026). */}
          {(() => {
            // Use visibleRows so these per-day totals respect an active
            // plant/supplier filter — matching the rows shown above and the
            // Total Hours footer (per Jillian, Aug 2026).
            const hourRows = visibleRows.filter((r) => {
              const ct = r.coverageType || 'Standard';
              return ct !== 'Mileage' && ct !== 'Expenses';
            });
            if (hourRows.length === 0) return null;
            // Billed Hours per Day — actual worked hours, OT 1:1.
            const dayHrs = (k) =>
              hourRows.reduce(
                (s, r) => s + (Number(r.values?.[k]) || 0),
                0,
              );
            // Commission Hours per Day — OT (Overage) at 1.5×, Standard 1:1.
            const commissionDayHrs = (k) =>
              hourRows.reduce((s, r) => {
                const v = Number(r.values?.[k]) || 0;
                const mult =
                  (r.coverageType || 'Standard') === 'Overage'
                    ? OT_RATE_MULTIPLIER
                    : 1;
                return s + v * mult;
              }, 0);
            const grand = DAY_KEYS.reduce((s, k) => s + dayHrs(k), 0);
            const commissionGrand = DAY_KEYS.reduce(
              (s, k) => s + commissionDayHrs(k),
              0,
            );
            const fmt = (n) => String(Math.round(n * 100) / 100);
            return (
              <tfoot>
                <tr
                  style={{
                    borderTop: '2px solid var(--brand-primary, #E87722)',
                  }}
                >
                  <td
                    colSpan={5}
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'var(--brand-muted)',
                      padding: '8px 6px',
                    }}
                  >
                    Billed Hours per Day
                  </td>
                  {DAY_KEYS.map((k) => (
                    <td
                      key={k}
                      style={{
                        textAlign: 'center',
                        fontWeight: 600,
                        color: 'var(--brand-ink)',
                      }}
                    >
                      {fmt(dayHrs(k))}
                    </td>
                  ))}
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: 'var(--brand-primary, #E87722)',
                    }}
                  >
                    {fmt(grand)}
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: 'var(--brand-muted)',
                      padding: '8px 6px',
                    }}
                  >
                    Commission Hours per Day
                  </td>
                  {DAY_KEYS.map((k) => (
                    <td
                      key={k}
                      style={{
                        textAlign: 'center',
                        fontWeight: 600,
                        color: 'var(--brand-ink)',
                      }}
                    >
                      {fmt(commissionDayHrs(k))}
                    </td>
                  ))}
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      color: 'var(--brand-primary, #E87722)',
                    }}
                  >
                    {fmt(commissionGrand)}
                  </td>
                </tr>
              </tfoot>
            );
          })()}
        </table>
      </div>

      <PlantNotesPanel timesheet={timesheet} />

      {/* Roll-up totals — same three-line treatment the submitter sees. */}
      {/* Footer row — parts footnote on the left (what the supplier
          ships), totals stacked on the right so the first supplier line
          ("Autosystems Belleville: Tail Lamps") visually sits next to
          the first totals line ("Total Hours: 12 hrs"). Border-top
          gives the whole row a single gray rule above it. The IRS rate
          note tucks in under the parts footnote on the left, so the
          bottom edge of the card is clean on the right. */}
      <div
        style={{
          marginTop: 14,
          padding: '12px 14px 4px',
          borderTop: '1px solid var(--brand-border, #e5e7eb)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {/* Legend — the parts list was removed (Jillian, Aug 2026) and
              replaced with a key explaining the orange row highlight. Shown
              only when a visible row's supplier bills weekly. */}
          {hasWeeklyRow && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                color: 'var(--brand-muted)',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  backgroundColor: '#fff7ed',
                  border: '1px solid #fdba74',
                  flex: '0 0 auto',
                }}
              />
              Suppliers that Bill Weekly
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            alignItems: 'flex-end',
            marginLeft: 'auto',
          }}
        >
          <TotalLine label="Total Hours" value={`${totalHours} hrs`} />
          {totalMiles > 0 && (
            <TotalLine
              label="Total Mileage"
              value={`$${totalMileageDollars.toFixed(2)} (${totalMiles} miles)`}
            />
          )}
          {totalExpenseDollars > 0 && (
            <TotalLine
              label="Expenses"
              value={`$${totalExpenseDollars.toFixed(2)}`}
            />
          )}
        </div>
      </div>
    </Wrapper>
  );
}

/**
 * Approved-history card. Always visible at the top, click to expand
 * the full read-only grid (the same one the Pending card shows, just
 * without Approve / Reject). Captures the two key audit timestamps
 * inline on the header so finance / RM can see at a glance when the
 * submission and the approval happened — no expanding required for
 * that part.
 */
function ApprovedCard({
  timesheet,
  supplierNameByCode,
  weeklyCoveragePairs,
  partsBySupplierCode,
  plants = [],
  suppliers = [],
  plantFilter = '',
  supplierFilter = '',
  coverageFilter = '',
  overagesOnly = false,
  expanded,
  onToggle,
  // RM / Director approved history (per Jillian, Aug 2026): skip the
  // collapsible summary header (submitter / plants / supplier list /
  // totals / timestamps) and show the tracker grid directly. The Liaison
  // Managers' own "Approved Trackers" view leaves trackerOnly false and
  // keeps its collapsible summary.
  trackerOnly = false,
  onRetract,
}) {
  if (trackerOnly) {
    return (
      <div className="card">
        {onRetract && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onRetract}
              title="Open this approved Billing Tracker in the editor. Saving replaces the approved version."
            >
              Retract &amp; Edit
            </button>
          </div>
        )}
        <ApprovalCard
          timesheet={timesheet}
          supplierNameByCode={supplierNameByCode}
          weeklyCoveragePairs={weeklyCoveragePairs}
                                      plants={plants}
                                      suppliers={suppliers}
                                      plantFilter={plantFilter}
                                      supplierFilter={supplierFilter}
                                      coverageFilter={coverageFilter}
                                  overagesOnly={overagesOnly}
          partsBySupplierCode={partsBySupplierCode}
          readOnly
        />
      </div>
    );
  }
  // Distinct plant codes and supplier codes that appear on the
  // timesheet, joined for the header line. Most timesheets are
  // single-plant / single-supplier; multi-plant or multi-supplier
  // weeks get rendered as "SHAP / OAC" + "Autosystems Belleville /
  // OAC Inc" so the RM can see at a glance what the submission
  // covers. Dedup is case-insensitive but the original casing is
  // preserved so the supplier-name lookup (keyed by SharePoint's
  // SupplierCode field) hits.
  const rows = Array.isArray(timesheet.rows) ? timesheet.rows : [];
  const distinctCodes = (field) => {
    const seen = new Set();
    const ordered = [];
    for (const r of rows) {
      const raw = String(r?.[field] || '').trim();
      if (!raw) continue;
      const upper = raw.toUpperCase();
      if (seen.has(upper)) continue;
      seen.add(upper);
      ordered.push(raw);
    }
    return ordered;
  };
  const plantsLabel = distinctCodes('plantCode').join(' / ') || '—';
  // Supplier names rather than codes for the header — the codes are
  // useful internally but the full names ("Autosystems Belleville")
  // read better in the at-a-glance summary. Fall back to the code if
  // a supplier somehow isn't in the lookup, so a missing record
  // doesn't blank the header.
  const suppliersLabel =
    distinctCodes('supplierCode')
      .map((code) => supplierNameByCode?.get(code) || code)
      .join(' / ') || '—';
  const totalHours = Number(timesheet.TotalHours) || 0;
  const totalMiles = Number(timesheet.TotalMiles) || 0;
  const totalMileageDollars =
    Number(timesheet.TotalMileageDollars) ||
    calcMileageDollars(totalMiles);
  const totalExpenseDollars = rows
    .filter((r) => r.coverageType === 'Expenses')
    .reduce((acc, r) => acc + (Number(r.expenseAmount) || 0), 0);
  // "Total Amount" on the header rolls up the dollar lines that the
  // expanded grid breaks out individually (mileage reimbursement +
  // expense receipts). Hours stay separate since they're a different
  // unit and finance reads them independently.
  const totalAmount = totalMileageDollars + totalExpenseDollars;
  return (
    <div className="card" style={{ padding: 0 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          color: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 14,
            fontWeight: 400,
            flex: '1 1 auto',
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 10,
              fontSize: 12,
              transition: 'transform 0.15s ease',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            ▶
          </span>
          {/* Title row — submitter name in brand orange, the rest in
              ink color, separated by gray middle-dot bullets. */}
          <span style={{ color: 'var(--brand-primary, #E87722)' }}>
            {timesheet.SubmitterName || 'Unknown submitter'}
          </span>
          {timesheet.IsSupplement && <SupplementPill />}
          {timesheet.IsCredit && <CreditPill />}
          <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
          <span>{plantsLabel}</span>
          <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
          <span>{totalHours} hrs</span>
          <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
          <span>${totalAmount.toFixed(2)}</span>
        </div>
        {/* Right-aligned timestamp block on the same row as the title.
            Wraps to its own line on narrow screens via the parent
            button's flexWrap. */}
        <div
          className="muted"
          style={{
            fontSize: 12,
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            textAlign: 'right',
            fontWeight: 400,
          }}
        >
          <span>
            Submitted:{' '}
            {timesheet.SubmittedAt
              ? formatTimestamp(timesheet.SubmittedAt)
              : '—'}
          </span>
          <span>
            Approved:{' '}
            {timesheet.ApprovedAt
              ? formatTimestamp(timesheet.ApprovedAt)
              : '—'}
            {timesheet.ApprovedBy ? ` by ${timesheet.ApprovedBy}` : ''}
          </span>
        </div>
      </button>
      {expanded && (
        <div
          style={{
            padding: '0 14px 14px',
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
          }}
        >
          <ApprovalCard
            timesheet={timesheet}
            supplierNameByCode={supplierNameByCode}
                                      weeklyCoveragePairs={weeklyCoveragePairs}
                                      plants={plants}
                                      suppliers={suppliers}
                                      plantFilter={plantFilter}
                                      supplierFilter={supplierFilter}
                                      coverageFilter={coverageFilter}
                                  overagesOnly={overagesOnly}
            partsBySupplierCode={partsBySupplierCode}
            readOnly
          />
        </div>
      )}
    </div>
  );
}

/**
 * Rejected-history card. Mirrors ApprovedCard's layout — collapsed
 * by default with a one-line summary, expand for the read-only grid
 * — but renders Rejected timestamps and the RM's rejection reason on
 * the header line so the watch-list reads at a glance. The rejection
 * reason is the actionable bit: it tells the submitter what to fix
 * before resubmitting.
 */
function RejectedCard({
  timesheet,
  supplierNameByCode,
  weeklyCoveragePairs,
  partsBySupplierCode,
  plants = [],
  suppliers = [],
  plantFilter = '',
  supplierFilter = '',
  coverageFilter = '',
  overagesOnly = false,
  expanded,
  onToggle,
}) {
  const rows = Array.isArray(timesheet.rows) ? timesheet.rows : [];
  // Dedup case-insensitively but preserve the original casing so
  // the supplier-name lookup (keyed by SupplierCode as stored on
  // the supplier record) hits.
  const distinctCodes = (field) => {
    const seen = new Set();
    const ordered = [];
    for (const r of rows) {
      const raw = String(r?.[field] || '').trim();
      if (!raw) continue;
      const upper = raw.toUpperCase();
      if (seen.has(upper)) continue;
      seen.add(upper);
      ordered.push(raw);
    }
    return ordered;
  };
  const plantsLabel = distinctCodes('plantCode').join(' / ') || '—';
  const suppliersLabel =
    distinctCodes('supplierCode')
      .map((code) => supplierNameByCode?.get(code) || code)
      .join(' / ') || '—';
  const totalHours = Number(timesheet.TotalHours) || 0;
  const totalMiles = Number(timesheet.TotalMiles) || 0;
  const totalMileageDollars =
    Number(timesheet.TotalMileageDollars) ||
    calcMileageDollars(totalMiles);
  const totalExpenseDollars = rows
    .filter((r) => r.coverageType === 'Expenses')
    .reduce((acc, r) => acc + (Number(r.expenseAmount) || 0), 0);
  const totalAmount = totalMileageDollars + totalExpenseDollars;
  return (
    <div className="card" style={{ padding: 0 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          color: 'inherit',
        }}
      >
        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              fontWeight: 400,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 10,
                fontSize: 12,
                transition: 'transform 0.15s ease',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              ▶
            </span>
            <span style={{ color: 'var(--brand-primary, #E87722)' }}>
              {timesheet.SubmitterName || 'Unknown submitter'}
            </span>
            <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
            <span>{plantsLabel}</span>
            <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
            <span>{suppliersLabel}</span>
            <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
            <span>{totalHours} hrs</span>
            <span style={{ color: 'var(--brand-muted, #94a3b8)' }}>·</span>
            <span>${totalAmount.toFixed(2)}</span>
          </div>
          {timesheet.RejectionReason ? (
            <div
              style={{
                fontSize: 12,
                marginLeft: 18,
                color: '#7c2d12',
                fontWeight: 400,
                whiteSpace: 'pre-wrap',
              }}
            >
              Reason: {timesheet.RejectionReason}
            </div>
          ) : null}
        </div>
        {/* Right-aligned timestamp block on the same row as the title.
            Wraps to its own line on narrow screens via the parent's
            flexWrap. */}
        <div
          className="muted"
          style={{
            fontSize: 12,
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            textAlign: 'right',
            fontWeight: 400,
          }}
        >
          <span>
            Submitted:{' '}
            {timesheet.SubmittedAt
              ? formatTimestamp(timesheet.SubmittedAt)
              : '—'}
          </span>
          <span>
            Rejected:{' '}
            {timesheet.RejectedAt
              ? formatTimestamp(timesheet.RejectedAt)
              : '—'}
            {timesheet.RejectedBy ? ` by ${timesheet.RejectedBy}` : ''}
          </span>
        </div>
      </button>
      {expanded && (
        <div
          style={{
            padding: '0 14px 14px',
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
          }}
        >
          <ApprovalCard
            timesheet={timesheet}
            supplierNameByCode={supplierNameByCode}
                                      weeklyCoveragePairs={weeklyCoveragePairs}
                                      plants={plants}
                                      suppliers={suppliers}
                                      plantFilter={plantFilter}
                                      supplierFilter={supplierFilter}
                                      coverageFilter={coverageFilter}
                                  overagesOnly={overagesOnly}
            partsBySupplierCode={partsBySupplierCode}
            readOnly
          />
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible week-level wrapper inside an RM's group. Header is the
 * full clickable bar (chevron + "Week of …" label + submission
 * count); body is whatever the caller passes as children, only
 * rendered when expanded. Default-open / default-closed behavior is
 * driven by the parent (Pending opens by default, Approved doesn't),
 * so this component just reflects the prop.
 */
/**
 * Plant-level collapsible group inside an RM section. Sits between
 * RmGroupHeader (the section banner) and WeekGroup (the inner week
 * blocks). Slightly heavier visual treatment than WeekGroup — a thin
 * left border in brand orange — so the two-level nesting reads
 * cleanly (Plant outside, Week inside) without confusion. Defaults
 * collapsed so the page lands compact; an RM scanning the list sees
 * only the plant headers and clicks into the ones they care about.
 */
function PlantGroup({ plantCode, count, expanded, onToggle, children }) {
  // Empty plant code = the unified passthrough (Aug 2026): the approval
  // list no longer splits a tracker by plant, so render the week groups
  // directly with no plant chip or collapse layer.
  if (!plantCode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {children}
      </div>
    );
  }
  const isSynthetic = plantCode.startsWith('(');
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: '#fff',
          border: '1px solid var(--brand-border, #e5e7eb)',
          borderLeft: isSynthetic
            ? '3px solid var(--brand-muted, #94a3b8)'
            : '3px solid var(--brand-primary, #f06a25)',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          textAlign: 'left',
          color: 'var(--brand-ink)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 10,
              fontSize: 11,
              transition: 'transform 0.15s ease',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            ▶
          </span>
          {/* Plant code rendered with the same purple "plant" badge
              treatment used elsewhere in the portal, so the eye
              recognizes it as a plant identifier. Synthetic
              buckets ("(Credits)", "(No plant)") get a neutral
              label instead. */}
          {isSynthetic ? (
            <span style={{ color: 'var(--brand-muted)' }}>{plantCode}</span>
          ) : (
            <span className="badge plant">{plantCode}</span>
          )}
        </span>
        <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>
          {count} {count === 1 ? 'entry' : 'entries'}
        </span>
      </button>
      {expanded && (
        <div
          style={{
            marginTop: 8,
            marginLeft: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function WeekGroup({ weekStart, count, expanded, onToggle, children }) {
  const dates = weekStart ? weekDates(weekStart) : null;
  const label = dates ? `Week of ${formatWeekRange(dates)}` : 'Undated week';
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
          textAlign: 'left',
          background: 'var(--brand-surface, #f8fafc)',
          border: '1px solid var(--brand-border, #e5e7eb)',
          color: 'var(--brand-ink)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-block',
              width: 10,
              fontSize: 11,
              transition: 'transform 0.15s ease',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            ▶
          </span>
          {label}
        </span>
        <span className="muted" style={{ fontWeight: 400 }}>
          {count} {count === 1 ? 'entry' : 'entries'}
        </span>
      </button>
      {expanded && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
}

/**
 * Sub-heading rendered above each Regional Manager's group of cards
 * on the Pending and Approved lists. Includes the RM name and a
 * count of cards in that group so the eye can size up workload
 * before scrolling. Visual treatment is intentionally lighter than
 * the section h2 so the two-level hierarchy reads correctly.
 */
function RmGroupHeader({ rm, count }) {
  return (
    <div
      style={{
        margin: '0 0 8px',
        padding: '6px 10px',
        background: 'var(--brand-surface, #f8fafc)',
        borderLeft: '3px solid var(--brand-primary, #E87722)',
        borderRadius: 4,
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--brand-ink)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
      }}
    >
      <span>{rm}</span>
      <span className="muted" style={{ fontWeight: 400 }}>
        {count} {count === 1 ? 'entry' : 'entries'}
      </span>
    </div>
  );
}

/**
 * Collect each distinct supplier that appears in the timesheet's rows,
 * then render a small italic line listing its parts (comma-separated).
 * Suppliers without any parts on file are skipped rather than emitted
 * as "(no parts)" — the whole footnote block disappears when nothing's
 * worth printing, so a timesheet made entirely of Expenses or Mileage
 * rows stays tidy.
 */
function ApprovalPartsFootnote({
  timesheet,
  supplierNameByCode,
  partsBySupplierCode,
}) {
  const rows = Array.isArray(timesheet.rows) ? timesheet.rows : [];
  const supplierCodes = [];
  const seen = new Set();
  for (const r of rows) {
    const code = r.supplierCode;
    if (!code || seen.has(code)) continue;
    seen.add(code);
    supplierCodes.push(code);
  }
  const blocks = supplierCodes
    .map((code) => ({
      code,
      name: supplierNameByCode?.get(code) || code,
      parts: partsBySupplierCode?.get(code) || [],
    }))
    .filter((b) => b.parts.length > 0);
  if (blocks.length === 0) return null;
  return (
    <div
      style={{
        fontSize: 12,
        fontStyle: 'italic',
        color: 'var(--brand-muted)',
        lineHeight: 1.5,
        flex: '1 1 auto',
        minWidth: 0,
      }}
    >
      {blocks.map((b) => (
        <div key={b.code}>
          <strong style={{ fontWeight: 600, fontStyle: 'normal' }}>
            {b.name}:
          </strong>{' '}
          {b.parts.join(', ')}
        </div>
      ))}
    </div>
  );
}

// ---- Helpers --------------------------------------------------------

/**
 * Weekly Notes by Plant — the free-text notes the submitter left on each
 * plant's block of the Billing Tracker (per Jillian, Aug 2026). Reads
 * timesheet.plantNotes ({ PLANTCODE: 'note…' }); renders nothing when
 * there are no notes so it never adds an empty box to a card.
 */
// ---- Billing totals tiles (mirrors the Billing Manager "Totals"
// block, reused here on the Approvals tab per Jillian, Aug 2026) -------
// Thousands-separated formatters for the Totals tiles so large numbers
// read easily (e.g. "$15,457.40", "12,345.6").
function fmtMoney(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function fmtNum(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function TotalTile({ label, value, unit, detail, highlighted }) {
  // Shrink the value font as the number gets longer so a comma-formatted
  // total still fits inside the tile.
  const valLen = String(value).length;
  const valueFontSize = valLen > 11 ? 22 : valLen > 8 ? 25 : 28;
  const cardStyle = highlighted
    ? {
        background: 'var(--brand-primary)',
        border: '1px solid var(--brand-primary)',
        marginTop: 0,
      }
    : { marginTop: 0 };
  const labelColor = highlighted
    ? 'rgba(255, 255, 255, 0.85)'
    : 'var(--brand-muted)';
  const valueColor = highlighted ? '#ffffff' : 'var(--brand-ink)';
  const detailColor = highlighted
    ? 'rgba(255, 255, 255, 0.85)'
    : 'var(--brand-muted)';
  return (
    <div className="card" style={cardStyle}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: labelColor,
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: valueFontSize, fontWeight: 700, color: valueColor, marginTop: 4 }}
      >
        {value}
        {unit && (
          <span
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginLeft: 6,
              color: valueColor,
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {detail && (
        <div style={{ fontSize: 12, marginTop: 6, color: detailColor }}>
          {detail}
        </div>
      )}
    </div>
  );
}

function countUniquePlants(billing) {
  const seen = new Set();
  for (const s of billing.suppliers || []) {
    for (const p of s.plants || []) {
      if (p.plantCode) seen.add(p.plantCode);
    }
  }
  return seen.size;
}

function hoursTotalDetail(billing) {
  // The big number is billed hours; show the commission-hours total (OT
  // at 1.5×) right below it in parentheses (per Jillian, Sep 2026).
  const commission = billing.grandTotalCommissionHours;
  if (commission == null) return '';
  return `(Total Commission Hours: ${fmtNum(commission)})`;
}

function periodTotalDetail(billing) {
  if (!billing.suppliers || billing.suppliers.length === 0) return '';
  const plantCodes = new Set();
  for (const s of billing.suppliers) {
    for (const p of s.plants || []) {
      const code = String(p.plantCode || '').trim().toUpperCase();
      if (code) plantCodes.add(code);
    }
  }
  const plantCount = plantCodes.size;
  return `${billing.suppliers.length} supplier${
    billing.suppliers.length === 1 ? '' : 's'
  } · ${plantCount} plant${plantCount === 1 ? '' : 's'}`;
}

// Per-tracker date + total line shown above each tracker now that the
// approvals lists no longer bucket trackers into collapsible "Week of…"
// groups — each tracker is listed on its own with the exact dates it
// covers and its hours total (per Jillian, Aug 2026).
// Directors see trackers grouped by Regional Manager, so each tracker's
// header also names who submitted it (per Jillian, Oct 2026). RMs already
// see trackers grouped by submitter, so they don't need it repeated.
function TrackerDateHeader({ timesheet, showSubmitter = false }) {
  const dates = timesheet.WeekStart ? weekDates(timesheet.WeekStart) : null;
  const label = dates ? formatWeekRange(dates) : 'Undated tracker';
  const hrs = Number(timesheet.TotalHours) || 0;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        flexWrap: 'wrap',
        padding: '10px 2px 2px',
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--brand-ink)',
      }}
    >
      <span>{label}</span>
      {showSubmitter && (
        <span style={{ color: 'var(--brand-muted)', fontWeight: 400 }}>
          · Submitted by{' '}
          <span style={{ color: 'var(--brand-ink)', fontWeight: 600 }}>
            {(timesheet.SubmitterName || '').trim() || 'Unknown submitter'}
          </span>
        </span>
      )}
      <span style={{ color: 'var(--brand-muted)', fontWeight: 400 }}>
        · {hrs} hrs
      </span>
    </div>
  );
}

function PlantNotesPanel({ timesheet }) {
  const notes =
    timesheet?.plantNotes && typeof timesheet.plantNotes === 'object'
      ? timesheet.plantNotes
      : {};
  const entries = Object.entries(notes).filter(([, v]) =>
    String(v || '').trim(),
  );
  if (entries.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 14px',
        border: '1px solid var(--brand-border, #e5e7eb)',
        borderRadius: 8,
        background: '#fafafa',
        fontSize: 13,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: 'var(--brand-ink)',
          marginBottom: 6,
        }}
      >
        Weekly Notes by Plant
      </div>
      {entries.map(([pc, text]) => (
        <div key={pc} style={{ marginBottom: 4 }}>
          <span style={{ fontWeight: 600 }}>{pc}: </span>
          <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
        </div>
      ))}
    </div>
  );
}

function TotalLine({ label, value }) {
  return (
    <div>
      <span style={{ marginRight: 6 }}>{label}:</span>
      <span
        style={{
          fontWeight: 700,
          color: 'var(--brand-primary, #E87722)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function formatWeekRange(dates) {
  if (!dates || dates.length < 7) return '';
  const first = dates[0];
  const last = dates[6];
  const sameMonth = first.getMonth() === last.getMonth();
  const fmt = (d, withMonth) =>
    d.toLocaleDateString(undefined, {
      month: withMonth ? 'short' : undefined,
      day: 'numeric',
    });
  return `${fmt(first, true)} – ${fmt(last, !sameMonth)}, ${last.getFullYear()}`;
}

function formatTimestamp(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Preset-range button. Renders the orange "primary" treatment when
// this button's preset matches the currently active one, and the
// neutral outline treatment otherwise. The selected look mirrors how
// the rest of the portal indicates a primary action (orange fill,
// white text) so the convention reads consistently across pages.
// Small pill rendered next to a submitter's name on an adjustment
// card. Two flavors: orange Supplement (adds money to the customer
// bill) and blue Credit (takes money off). Same shape/sizing as the
// OT badges elsewhere on the page for visual consistency.
function SupplementPill() {
  return (
    <AdjustmentPill
      label="Supplement"
      background="var(--brand-primary, #E87722)"
    />
  );
}

function CreditPill() {
  return <AdjustmentPill label="Credit" background="#2563eb" />;
}

function AdjustmentPill({ label, background }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        background,
        color: '#fff',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </span>
  );
}

// Reason + Billing Disposition footnote shown directly under an
// adjustment card's header. Reads as a single sentence so the
// approver can scan it without parsing labels. Credits also surface
// the credit amount up front since that's the most important number
// on the card. The disposition label pulls from the shared
// BILLING_DISPOSITION_LABELS map so the wording stays in sync with
// the form.
function SupplementContext({ timesheet }) {
  const reason = (
    timesheet.AdjustmentReason ||
    timesheet.SupplementReason ||
    ''
  ).trim();
  const disposition = timesheet.BillingDisposition || '';
  const dispLabel = ADJUSTMENT_DISPOSITION_SHORT_LABEL[disposition] || '';
  const isCredit = Boolean(timesheet.IsCredit);
  const creditAmount = Number(timesheet.CreditAmount) || 0;
  if (!reason && !dispLabel && !(isCredit && creditAmount > 0)) return null;
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 12,
        color: 'var(--brand-muted)',
        fontStyle: 'italic',
      }}
    >
      {isCredit && creditAmount > 0 && (
        <>
          <strong style={{ fontStyle: 'normal' }}>
            −${creditAmount.toFixed(2)}
          </strong>
          {' · '}
        </>
      )}
      {reason && <>“{reason}”</>}
      {reason && dispLabel && ' · '}
      {dispLabel && (
        <span>{isCredit ? 'Credit via: ' : 'Billing: '}{dispLabel}</span>
      )}
    </div>
  );
}

// Short, scannable labels for every BillingDisposition. Full labels
// live in BILLING_DISPOSITION_LABELS (used in the form); these
// abbreviated versions read better inline on the approval card.
const ADJUSTMENT_DISPOSITION_SHORT_LABEL = {
  'supplemental-invoice': 'supplemental invoice',
  'next-invoice': 'next invoice (PPA)',
  'credit-memo': 'credit memo',
  'next-invoice-credit': 'next invoice (credit line)',
};

function PresetButton({ preset, active, onClick, children }) {
  const isActive = active === preset;
  return (
    <button
      type="button"
      className={isActive ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
      aria-pressed={isActive}
      onClick={() => onClick(preset)}
    >
      {children}
    </button>
  );
}

// "Apr 22, 2026 – May 13, 2026" label rendered beside the date
// pickers. Parses each ISO string as local-time components rather
// than letting `new Date(iso)` interpret it as UTC midnight (which
// shifts the rendered day back one in negative-offset timezones).
function formatApprovalsRangeLabel(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const fmt = (iso) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  return `${fmt(startIso)} – ${fmt(endIso)}`;
}

// Re-export the RM-only helpers so tests or other pages can reach them
// without pulling directly from the service.
export const __rmHelpers = { approveTimesheet, rejectTimesheet, updateApprovedTimesheet };
